import { useEffect } from 'react'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { reportError } from '@/lib/error-reporter'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

const CHUNK_ERROR_PATTERNS = [
  'is not a valid JavaScript MIME type',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
]

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }
  return CHUNK_ERROR_PATTERNS.some((p) => error.message.includes(p))
}

function attemptChunkReload(): boolean {
  try {
    const key = 'chunk-reload-attempted'
    if (sessionStorage.getItem(key)) {
      return false
    }
    sessionStorage.setItem(key, '1')
    window.location.reload()
    return true
  } catch {
    return false
  }
}

type GeneralErrorProps = React.HTMLAttributes<HTMLDivElement> & {
  minimal?: boolean
  error?: unknown
}

export function GeneralError({
  className,
  minimal = false,
  error,
}: GeneralErrorProps) {
  const navigate = useNavigate()
  const { history } = useRouter()

  // Handle stale deployment chunk errors with a one-time reload
  useEffect(() => {
    if (error && isChunkLoadError(error)) {
      attemptChunkReload()
    }
  }, [error])

  // Report error to Sentry when component mounts
  useEffect(() => {
    if (error) {
      if (!isChunkLoadError(error)) {
        reportError(error, { toast: false })
      }
    } else {
      reportError(new Error('500 error page rendered without error object'), {
        toast: false,
      })
    }
  }, [error])
  return (
    <div className={cn('h-svh w-full', className)}>
      <div className='m-auto flex h-full w-full flex-col items-center justify-center gap-2'>
        {!minimal && (
          <h1 className='text-[7rem] leading-tight font-bold'>500</h1>
        )}
        <span className='font-medium'>Oops! Something went wrong {`:')`}</span>
        <p className='text-muted-foreground text-center'>
          We apologize for the inconvenience. <br /> Please try again later.
        </p>
        {!minimal && (
          <div className='mt-6 flex gap-4'>
            <Button variant='outline' onClick={() => history.go(-1)}>
              Go Back
            </Button>
            <Button onClick={() => navigate({ to: '/' })}>Back to Home</Button>
          </div>
        )}
      </div>
    </div>
  )
}
